import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faExpand, faEye, faFilm, faVideo } from '@fortawesome/free-solid-svg-icons';
import { Renderer, type CameraView, type Drawing } from '@hi-level/3d-renderer';

interface Floor3DViewProps {
  drawing: Drawing | null;
  onError?: (error: unknown) => void;
  className?: string;
}

const CAMERA_VIEWS: CameraView[] = ['top', 'front', 'rear', 'left', 'right', 'isometric'];

const VISIBILITY_OPTIONS: Array<{ label: string; toggle: (r: Renderer) => void }> = [
  { label: 'Decking', toggle: (r) => r.toggleDecking() },
  { label: 'Shadows', toggle: (r) => r.toggleShadows() },
];

export function Floor3DView({ drawing, onError, className }: Floor3DViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const onErrorRef = useRef(onError);

  const [buttonsEnabled, setButtonsEnabled] = useState(false);
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false);
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const [cinematic, setCinematic] = useState(false);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !drawing) return;
    const renderer = new Renderer({
      containerElement: container,
      baseUrl: '/models/',
      enableShadows: true,
      onError: (error) => onErrorRef.current?.(error),
    });
    rendererRef.current = renderer;
    if (renderer.canRender) {
      renderer.loadFloor(drawing);
      renderer.enableKeyboardShortcuts();
      setButtonsEnabled(true);
    } else {
      setButtonsEnabled(false);
    }
    return () => {
      renderer.destroy?.();
      rendererRef.current = null;
      setButtonsEnabled(false);
      setShowVisibilityMenu(false);
      setShowCameraMenu(false);
      setCinematic(false);
    };
  }, [drawing]);

  const toggleVisibilityMenu = useCallback(() => {
    setShowVisibilityMenu((v) => !v);
    setShowCameraMenu(false);
  }, []);

  const toggleCameraMenu = useCallback(() => {
    setShowCameraMenu((v) => !v);
    setShowVisibilityMenu(false);
  }, []);

  const handleVisibilityToggle = useCallback((toggle: (r: Renderer) => void) => {
    const r = rendererRef.current;
    if (!r) return;
    toggle(r);
  }, []);

  const handleFitToView = useCallback(() => {
    rendererRef.current?.fitToView();
  }, []);

  const handleToggleCinematic = useCallback(() => {
    setCinematic((prev) => {
      const next = !prev;
      rendererRef.current?.toggleCinematic(next);
      return next;
    });
  }, []);

  const handleSetCameraView = useCallback((view: CameraView) => {
    rendererRef.current?.setCameraView(view);
    setShowCameraMenu(false);
  }, []);

  const handleSnapshot = useCallback(() => {
    const imageData = rendererRef.current?.getImageData();
    if (!imageData) return;
    const link = document.createElement('a');
    link.download = 'floor.png';
    link.href = imageData;
    link.click();
  }, []);

  const disabled = !buttonsEnabled;

  return (
    <div
      className={
        className ??
        'relative w-full h-[60vh] min-h-[360px] max-h-[800px] sm:min-h-[420px] lg:h-[640px] xl:h-[760px]'
      }
    >
      <div ref={containerRef} className="absolute inset-0" />

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center">
        {showVisibilityMenu && (
          <div className="flex flex-wrap gap-1 rounded-2xl bg-hilevel-slate/85 backdrop-blur-md p-2 mb-2 shadow-xl ring-1 ring-white/10 justify-center">
            {VISIBILITY_OPTIONS.map((opt) => (
              <button
                key={opt.label}
                type="button"
                onClick={() => handleVisibilityToggle(opt.toggle)}
                disabled={disabled}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-white/90 hover:bg-white/10 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed border-0 bg-transparent"
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
        {showCameraMenu && (
          <div className="flex flex-wrap gap-1 rounded-2xl bg-hilevel-slate/85 backdrop-blur-md p-2 mb-2 shadow-xl ring-1 ring-white/10 justify-center">
            {CAMERA_VIEWS.map((view) => (
              <button
                key={view}
                type="button"
                onClick={() => handleSetCameraView(view)}
                disabled={disabled}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-white/90 hover:bg-white/10 capitalize cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed border-0 bg-transparent"
              >
                {view}
              </button>
            ))}
            <ToolbarButton
              onClick={handleFitToView}
              disabled={disabled}
              title="Fit to view"
              icon={<FontAwesomeIcon icon={faExpand} className="w-4 h-4" />}
            />
            <ToolbarButton
              onClick={handleToggleCinematic}
              disabled={disabled}
              active={cinematic}
              title="Cinematic"
              icon={<FontAwesomeIcon icon={faFilm} className="w-4 h-4" />}
            />
          </div>
        )}

        <div className="flex items-center gap-2 rounded-2xl bg-hilevel-slate/85 backdrop-blur-md px-3 py-2 shadow-xl ring-1 ring-white/10">
          <ToolbarButton
            onClick={toggleVisibilityMenu}
            disabled={disabled}
            title="Visibility"
            icon={<FontAwesomeIcon icon={faEye} className="w-4 h-4" />}
          />
          <ToolbarButton
            onClick={toggleCameraMenu}
            disabled={disabled}
            title="Camera views"
            icon={<FontAwesomeIcon icon={faVideo} className="w-4 h-4" />}
          />
          <div className="w-px h-6 bg-white/20 mx-1" />
          <ToolbarButton
            onClick={handleSnapshot}
            disabled={disabled}
            icon={<FontAwesomeIcon icon={faCamera} className="w-4 h-4" />}
            label="Snapshot"
          />
        </div>
      </div>
    </div>
  );
}

interface ToolbarButtonProps {
  onClick: () => void;
  disabled?: boolean;
  icon: ReactNode;
  label?: string;
  title?: string;
  active?: boolean;
}

function ToolbarButton({ onClick, disabled, icon, label, title, active }: ToolbarButtonProps) {
  const base =
    'flex items-center gap-2 rounded-lg transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed border-0';
  const padding = label ? 'px-3 py-2 text-sm font-medium rounded-xl' : 'p-2';
  const tone = active
    ? 'bg-white/20 text-white'
    : 'text-white/90 hover:bg-white/10 active:bg-white/15 bg-transparent';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`${base} ${padding} ${tone}`}
    >
      {icon}
      {label && <span className="hidden sm:inline">{label}</span>}
    </button>
  );
}
